/* eslint-disable react-hooks/exhaustive-deps */
import { SelectChangeEvent } from '@mui/material'
import { useEffect, useState, useRef, useCallback } from 'react'
import { v4 as uuidv4 } from 'uuid'

import useCaptureCamera from 'views/DigitalTrayMapping/CaptureTray/CaptureCamera/CaptureCamera.logic'
import { useCaptureTrayContext } from 'views/DigitalTrayMapping/CaptureTray/CaptureTray.context'
import { useInventorySheetsContext } from '../InventorySheets/InventorySheets.context'
import { useAddTrayContext } from '../AddTray.context'
import { useSubTraysContext } from '../SubTrays/SubTrays.context'

import { InventorySheetFile } from '../InventorySheets/InventorySheets.types'
import { BetterIDTrayScrew } from 'views/SPDLayout/SPD/SPD.types'
import { getAnalysisJSONFileName } from 'lib/utils/getAnalysisJSONFile'
import { TrayType } from 'components/organisms/EditTrayModal/EditTrayModal.types'
import { SubTray } from '../SubTrays/SubTrays.types'
import { useSPD } from 'views/SPDLayout/SPD/SPD.context'
import useTrayMapLogic from 'views/SPDLayout/SPD/TrayMap/TrayMap.logic'
import { TRAY_PAR_LEVELS } from 'lib/dtm-manufacturers'
import { ScrewAnnotation } from 'components/organisms/GrayScale/GrayScale.types'
import { useTrayStepContext } from '../AddTray.logic'

interface TrayItem {
  trayType: string
  imageUrl: string
}

interface TraySet {
  traySetName: string
  trays: TrayItem[]
}

const useAnalyzeTrayLogic = () => {
  const {
    handleSPDCaptureImage,
    image,
    setImage,
    videoRef,
    mediaStream,
    cameras,
    activeCamera,
    fetchCameraData,
    isCameraMenuOpen,
    handleSelectCamera,
    handleToggleCameraMenu,
    orientation,
    videoHeight,
  } = useCaptureCamera()
  const { isSubmitting, setIsSubmitting } = useCaptureTrayContext()
  const {
    trayScrews,
    setTrayScrews,
    setTrayPlates,
    trayPlates,
    parLevel,
    setParLevel,
    otherItems,
    setOtherItems,
    customImplants,
    setCustomImplants,
  } = useSPD()
  const { findScrew } = useTrayMapLogic({ loadWithUDIs: false })
  const {
    setTrayAnalysisScrews,
    trayImage,
    setTrayImage,
    selectedTrayType,
    isAnalysisComplete,
    setIsAnalysisComplete,
    setAnalysisFileName,
    handleSubmitAnalysis,
    analysisScrews,
    setIsAnalysisFromSPD,
    isComplete,
    setIsComplete,
    analysisError,
    setAnalysisError,
    setSelectedTrayType,
    subTrays,
    setSubTrays,
    trayVendor,
    hasSubTrays,
    currentTrayIndex,
    updateCurrentTrayItem,
    saveCurrentTrayAsItem,
    templateData,
    setTemplateData,
    selectedTraySetName,
    setSelectedTraySetName,
  } = useAddTrayContext()
  const { uploadFile } = useInventorySheetsContext()
  const { setCurrentView } = useSubTraysContext()
  const { nextStep } = useTrayStepContext()

  const [isTrayMapDrawerOpen, setIsTrayMapDrawerOpen] = useState<boolean>(false)
  const [description, setDescription] = useState<string>('')
  const [trayTypesByVendor, setTrayTypesByVendor] = useState<
    Record<string, TraySet[]>
  >({})
  const [allTrayTypes, setAllTrayTypes] = useState<TraySet[]>([])
  const trayTypesFetched = useRef(false)
  const currentTrayTypes: TraySet[] = trayVendor
    ? trayTypesByVendor[trayVendor] || [] // use selected-vendor data
    : allTrayTypes // otherwise show everything
  const [isLoadingTrayTypes, setIsLoadingTrayTypes] = useState(true)
  const [isErrorModalOpen, setIsErrorModalOpen] = useState<boolean>(false)
  const [hasAnalyzed, setHasAnalyzed] = useState<boolean>(false)
  const errorHandled = useRef(false)
  const [apiParLevels, setApiParLevels] = useState<Record<string, number>>({})

  const [needsRotate, setNeedsRotate] = useState(false)

  const isTrayEmpty = trayScrews.length === 0

  const hasPlates =
    selectedTrayType === 'stryker middle face' ||
    selectedTrayType === 'stryker upper face'
  const shouldConfirmButtonBeDisabled =
    !trayImage || (trayImage && trayImage.isUploading) || isSubmitting
  const isInstrumentTray = selectedTrayType === 'instrument'
  const isZimmerTray = selectedTrayType?.includes('zimmer')
  const isCustomImplantTray = selectedTrayType === 'custom implants'

  /* trays that belong to the currently-picked tray-set */
  const traysForSelectedSet: TrayItem[] = selectedTraySetName
    ? currentTrayTypes.find((ts) => ts.traySetName === selectedTraySetName)
        ?.trays || []
    : []

  // a helper to get a nice label
  const renderTrayLabel = useCallback((tray: any) => {
    const label =
      tray.display_name ?? tray.trayLabel ?? tray.trayType ?? 'Unknown Tray'

    return (typeof label === 'string' ? label : 'Unknown Tray')
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (c: string) => c.toUpperCase())
  }, [])

  useEffect(() => {
    if (selectedTrayType) {
      const analysisFileName = getAnalysisJSONFileName(selectedTrayType)
      setAnalysisFileName(analysisFileName)
      setTrayScrews([])
      setTrayPlates([])
      setCustomImplants([])

      const fetchTemplateData = async () => {
        if (!selectedTrayType) {
          console.warn('No tray type selected, cannot fetch template data.')
          return
        }
        try {
          const encodedTrayType = encodeURIComponent(selectedTrayType)

          const baseUrl = process.env.REACT_APP_DTM_COMPUTER_VISION_API
          if (!baseUrl) {
            throw new Error(
              'REACT_APP_DTM_COMPUTER_VISION_API environment variable is not set.'
            )
          }
          const apiUrl = `${baseUrl.replace(/\/$/, '')}/api/tray-templates`
          const url = `${apiUrl}?tray_type=${encodedTrayType}`

          const response = await fetch(url)

          if (!response.ok) {
            let errorBody = `Status: ${response.status}`
            try {
              const errorJson = await response.json()
              errorBody =
                errorJson.detail ||
                errorJson.message ||
                JSON.stringify(errorJson)
            } catch (parseError) {
              console.error(
                'Failed to parse error response as JSON:',
                parseError
              )
            }
            throw new Error(`Failed to fetch template data: ${errorBody}`)
          }

          const data = await response.json()

          setTemplateData(data)

          // Use API par level if available, otherwise fall back to hardcoded par levels
          const defaultParLevel = TRAY_PAR_LEVELS[selectedTrayType] || 1
          const apiParLevel = data.par_level
          setParLevel(apiParLevel !== undefined ? apiParLevel : defaultParLevel)
        } catch (error) {
          console.error('Error fetching template data:', error)
          setTemplateData(null) // Clear template data on error
        }
      }

      fetchTemplateData()
    }
  }, [
    selectedTrayType,
    setAnalysisFileName,
    setTrayScrews,
    setTrayPlates,
    setTemplateData,
    apiParLevels,
  ])

  useEffect(() => {
    if (currentTrayIndex >= 0) {
      const currentTrayItem = subTrays[currentTrayIndex]
      if (currentTrayItem && !hasSubTrays) {
        setTrayImage({
          id: currentTrayItem.trayImg,
          src: currentTrayItem.trayImg,
          isUploading: false,
          isUploaded: true,
          mimeType: 'image/jpeg',
        })
        setTrayScrews(currentTrayItem.content.screws)
        setTrayPlates(currentTrayItem.content.plates)
        setCustomImplants(currentTrayItem.content.customImplants)
        setDescription(currentTrayItem.description)
        setSelectedTrayType(currentTrayItem.trayType)
        setParLevel(currentTrayItem.parLevel)
        setIsAnalysisComplete(true)
      }
    }
  }, [currentTrayIndex])

  useEffect(() => {
    if (image) {
      const saveTrayImage = async () => {
        const imgId: string = uuidv4()
        const newTrayImage: InventorySheetFile = {
          id: imgId,
          src: image,
          isUploading: true,
          isUploaded: false,
          mimeType: 'image/jpeg',
        }
        setTrayImage(newTrayImage)

        await uploadAndUpdateTrayImage(imgId)
      }

      saveTrayImage()
    }
  }, [image])

  useEffect(() => {
    if (isComplete) {
      handleAddScrewsToVendorSPDMap()
    }
  }, [isComplete])

  useEffect(() => {
    if (analysisError && !errorHandled.current) {
      errorHandled.current = true
      setIsErrorModalOpen(true)
      setIsSubmitting(false)
    }
  }, [analysisError])

  useEffect(() => {
    setIsAnalysisFromSPD(true)
  }, [])

  // Handle image rotation detection
  useEffect(() => {
    if (!trayImage?.src) return

    const img = new Image()
    img.onload = () => {
      // if width > height we assume the phone was held upright and the EXIF
      // orientation flag was lost ➜ rotate 90°
      setNeedsRotate(img.height > img.width)
    }
    img.src = trayImage.src
  }, [trayImage?.src])

  /**
   * Uploads the tray image and updates the tray image state.
   * @param {string} imgId - The ID of the image to upload.
   */
  const uploadAndUpdateTrayImage = async (imgId: string) => {
    try {
      const trayImageURL = await uploadFile(imgId, image, 'image/jpeg', false)

      if (trayImageURL) {
        const newTrayImage: InventorySheetFile = {
          id: imgId,
          src: trayImageURL,
          isUploading: false,
          isUploaded: true,
          mimeType: 'image/jpeg',
        }

        // Keep the safe state update pattern
        requestAnimationFrame(() => {
          setTimeout(() => {
            setTrayImage(newTrayImage)
          }, 150)
        })
      } else {
        setTrayImage(undefined)
      }
    } catch (error) {
      console.error('Error uploading tray image:', error)
      setTrayImage(undefined)
    }
  }

  const getTemplateData = (trayType: TrayType): ScrewAnnotation[] => {
    // If we have template data from the API, use it
    if (templateData && templateData.template_data) {
      // Extract the first key from the template data (the tray name)
      const trayKey = Object.keys(templateData.template_data)[0]
      if (
        trayKey &&
        templateData.template_data[trayKey] &&
        templateData.template_data[trayKey].screw
      ) {
        return templateData.template_data[trayKey].screw
      }
    }

    return []
  }

  /**
   * Handles adding screws to the vendor SPD map.
   */
  const handleAddScrewsToVendorSPDMap = () => {
    const templateScrews = getTemplateData(selectedTrayType)

    const mappedAnalysisScrews: BetterIDTrayScrew[] = analysisScrews.map(
      (analysisScrew: any) => {
        // Find matching screw in template array directly
        const templateScrew = templateScrews.find(
          (screw) =>
            screw.label === analysisScrew.label &&
            screw.column_label === analysisScrew.column
        ) as { device_id?: string; device_description?: string }

        return {
          row: analysisScrew.row,
          column: analysisScrew.column,
          label: analysisScrew.label,
          wasted: analysisScrew.wasted,
          x: analysisScrew.x,
          deviceId: templateScrew?.device_id,
          deviceDescription: templateScrew?.device_description,
          company: {
            name: 'Stryker',
            id: 0,
            regEmail: '',
          },
          expirationDate: null,
        }
      }
    )

    const newScrewsFromAnalysis: BetterIDTrayScrew[] =
      mappedAnalysisScrews.filter(
        (mappedAnalysisScrew) =>
          !findScrew(
            mappedAnalysisScrew.label,
            mappedAnalysisScrew.row,
            mappedAnalysisScrew.x
          )
      )

    setTrayScrews(newScrewsFromAnalysis)
    setTrayAnalysisScrews(newScrewsFromAnalysis)
    setIsComplete(false)
    setIsAnalysisComplete(true)
    setIsTrayMapDrawerOpen(true)
  }

  /**
   * Handles click event for analysis.
   */
  const handleAnalysisClick = () => {
    if (isAnalysisComplete || isCustomImplantTray) {
      setIsTrayMapDrawerOpen(true)
      return
    }
    setHasAnalyzed(true)
    setAnalysisError('')
    if (trayImage) {
      handleSubmitAnalysis(trayImage.src)
    }
  }

  /**
   * Handles closing the tray map drawer.
   */
  const handleTrayMapDrawerClose = () => {
    setIsTrayMapDrawerOpen(false)
  }

  /**
   * Handles capturing the tray image.
   */
  const handleImageCapture = (skipRotate?: boolean) => {
    setIsAnalysisComplete(false)
    handleSPDCaptureImage(skipRotate)
  }

  /**
   * Handles changes to the tray type.
   * @param e - The event object for the select change.
   */
  const handleTrayTypeChange = (e: SelectChangeEvent<string>) => {
    const newSelectedTrayType = e.target.value.toLowerCase() as TrayType
    setSelectedTrayType(newSelectedTrayType)
    setTemplateData(null) // Clear template data when changing tray type

    if (newSelectedTrayType === 'zimmer curved rods') return setParLevel(2)

    if (
      newSelectedTrayType === 'zimmer adjustable transverse connector' ||
      newSelectedTrayType === 'zimmer standard closure tops'
    )
      return setParLevel(1)

    setParLevel(0)
  }

  /**
   * Handles the user's back click.
   */
  const handleBackClick = () => {
    setSelectedTrayType(undefined)
    setTrayImage(undefined)
    setTrayScrews([])
    setTrayPlates([])
    setCustomImplants([])
    setCurrentView('list')
  }

  /**
   * Handles the logic for adding a new sub-tray.
   *
   * - Generates a new tray number based on the current number of sub-trays.
   * - Creates a new sub-tray object if `trayImage` is available, using the selected tray type and default par level.
   * - Adds the newly created sub-tray to the existing list of sub-trays and updates the state.
   *
   * @remarks
   * The new sub-tray is only added if a valid `trayImage` is present.
   *
   * @see handleConfirm
   */
  const handleAddSubTray = () => {
    const trayNumber = subTrays.length + 1

    if (trayImage) {
      const newSubTray: SubTray = {
        trayNumber,
        trayImg: trayImage.src,
        trayType: selectedTrayType,
        parLevel,
        content: {
          screws: trayScrews,
          plates: trayPlates,
          otherItems,
          customImplants,
        },
        isInstrumentTray,
        description,
      }

      const newSubTrays = [...subTrays, newSubTray]

      setSubTrays(newSubTrays)
    }
  }

  /**
   * Handles the user induced confirmation action.
   *
   * - Adds a sub tray via `handleAddSubTray`.
   * - Resets state for next sub tray
   * - Navigates back to list view
   */
  const handleConfirm = () => {
    const trayNumber = subTrays.length + 1
    const newSubTray: SubTray = {
      trayNumber,
      trayImg: trayImage?.src || '',
      trayType: selectedTrayType,
      parLevel,
      content: {
        screws: trayScrews,
        plates: trayPlates,
        otherItems,
        customImplants,
      },
      isInstrumentTray,
      description,
    }

    // If we're editing an existing tray, update it
    if (currentTrayIndex >= 0) {
      if (hasSubTrays) {
        updateCurrentTrayItem()
        setCurrentView('list')
      } else {
        updateCurrentTrayItem([newSubTray])
        nextStep()
      }
      return
    } else if (!hasSubTrays) {
      saveCurrentTrayAsItem(newSubTray)
    }

    handleAddSubTray()

    resetStateForNextSubTray()

    if (hasSubTrays) {
      setCurrentView('list')
    } else {
      nextStep()
    }
  }

  const resetStateForNextSubTray = () => {
    setSelectedTrayType(undefined)
    setTrayImage(undefined)
    setTrayScrews([])
    setTrayPlates([])
    setCustomImplants([])
    setIsAnalysisComplete(false)
    setAnalysisError('')
    setParLevel(0)
    setDescription('')
    setCurrentView('list')
  }

  /**
   * Handles the onChange event for the Par Level Textfield.
   *
   * This function updates the `parLevel` with the numeric value from the input field.
   *
   * @param {React.ChangeEvent<HTMLInputElement>} event - The change event from the number input field.
   */
  const handleTrayParLevelChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.target.value
    const parLevelValue = value !== '' ? parseFloat(value) : 0

    setParLevel(parLevelValue)
  }

  const handleDescriptionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDescription(e.target.value)
  }

  const recapture = () => {
    setTrayImage(undefined)
    setTrayScrews([])
    setTrayPlates([])
    setCustomImplants([])
    fetchCameraData()
  }

  const showRecaptureButton = !!trayImage // Only show recapture when there's an image

  /**
   * Handles the user's choice to retry image capture
   */
  const handleRetryCapture = () => {
    setIsErrorModalOpen(false)
    setAnalysisError('')
    errorHandled.current = false
    recapture()
  }

  /**
   * Handles the user's choice to proceed manually
   */
  const handleProceedManually = () => {
    setIsErrorModalOpen(false)
    setAnalysisError('')
    errorHandled.current = false
    setIsTrayMapDrawerOpen(true)
  }

  // Fetch all available tray types from the API
  useEffect(() => {
    if (trayTypesFetched.current) return

    const fetchAllTrayTypes = async () => {
      setIsLoadingTrayTypes(true)
      try {
        const baseUrl = process.env.REACT_APP_DTM_COMPUTER_VISION_API
        if (!baseUrl) {
          throw new Error(
            'REACT_APP_DTM_COMPUTER_VISION_API environment variable is not set.'
          )
        }

        const apiUrl = `${baseUrl.replace(/\/$/, '')}/api/tray-templates`
        const response = await fetch(apiUrl)

        if (!response.ok) {
          throw new Error(`Failed to fetch tray types: ${response.status}`)
        }

        const data = await response.json()

        if (data && typeof data === 'object') {
          // vendor → [ TraySet ]
          const vendorMapping: Record<string, TraySet[]> = {}
          const parLevelMapping: Record<string, number> = {}

          Object.entries(data).forEach(([vendor, trayItems]) => {
            const normalizedVendor = vendor.toLowerCase()

            // traySetName → TrayItem[]
            const traySetMap: Record<string, TrayItem[]> = {}

            if (Array.isArray(trayItems)) {
              trayItems.forEach((item: any) => {
                if (
                  item &&
                  typeof item === 'object' &&
                  'tray_type' in item &&
                  'tray_set_name' in item &&
                  'image_url' in item
                ) {
                  const traySetName = item.tray_set_name
                  const trayType = (item.tray_type as string).toLowerCase()
                  const imageUrl = item.image_url as string

                  if (!traySetMap[traySetName]) traySetMap[traySetName] = []
                  traySetMap[traySetName].push({ trayType, imageUrl })

                  // keep par level map if supplied by API
                  if (
                    'par_level' in item &&
                    typeof item.par_level === 'number'
                  ) {
                    parLevelMapping[trayType] = item.par_level
                  }
                }
              })
            }

            // Convert map → array for easy rendering
            const traySets: TraySet[] = Object.entries(traySetMap).map(
              ([traySetName, trays]) => ({ traySetName, trays })
            )

            vendorMapping[normalizedVendor] = traySets
          })

          setTrayTypesByVendor(vendorMapping)

          // Flatten all vendors → single array of TraySet
          const allTypesList: TraySet[] = Object.values(vendorMapping).flat()
          setAllTrayTypes(allTypesList)

          setApiParLevels(parLevelMapping)
        } else {
          setAllTrayTypes([])
          setTrayTypesByVendor({})
        }

        trayTypesFetched.current = true
      } catch (error) {
        console.error('Error fetching tray types:', error)
        setAllTrayTypes([])
        setTrayTypesByVendor({})
      } finally {
        setIsLoadingTrayTypes(false)
      }
    }

    fetchAllTrayTypes()
  }, [])

  const handleTraySetChange = (e: SelectChangeEvent<string>) => {
    setSelectedTraySetName(e.target.value)
    setSelectedTrayType(undefined) // reset any previously chosen tray
  }

  const handleTrayTypeSelection = (trayType: string) => {
    setSelectedTrayType(trayType as TrayType)
  }

  return {
    handleImageCapture,
    image,
    setImage,
    videoRef,
    mediaStream,
    cameras,
    activeCamera,
    isCameraMenuOpen,
    handleSelectCamera,
    handleToggleCameraMenu,
    isTrayMapDrawerOpen,
    handleTrayMapDrawerClose,
    handleAnalysisClick,
    isSubmitting,
    isAnalysisComplete,
    handleTrayTypeChange,
    handleBackClick,
    isTrayEmpty,
    handleConfirm,
    shouldConfirmButtonBeDisabled,
    hasPlates,
    parLevel,
    handleTrayParLevelChange,
    isInstrumentTray,
    description,
    handleDescriptionChange,
    isZimmerTray,
    recapture,
    currentTrayTypes,
    isErrorModalOpen,
    handleRetryCapture,
    handleProceedManually,
    showRecaptureButton,
    trayScrews,
    trayPlates,
    hasAnalyzed,
    setHasAnalyzed,
    orientation,
    videoHeight,
    resetStateForNextSubTray,
    otherItems,
    setOtherItems,
    allTrayTypes,
    isLoadingTrayTypes,
    isCustomImplantTray,
    selectedTrayType,
    selectedTraySetName,
    handleTraySetChange,
    traysForSelectedSet,
    handleTrayTypeSelection,
    renderTrayLabel,
    needsRotate,
    setNeedsRotate,
  }
}

export default useAnalyzeTrayLogic
